// Permisos del llamador y errores tipados de las pantallas de IA del helpdesk.
//
// El backend nuevo responde 403 con un cuerpo { code, message, capabilities? }:
// traducir ese `code` a una frase que el admin entienda es trabajo de ESTE
// módulo, no de cada pantalla. Un código desconocido —o un backend viejo, que
// solo manda `message`— cae al mensaje del servidor tal cual: el servidor habla
// español y las claves i18n de acá son la interfaz, no su texto.
//
// El mismo cuerpo trae `caller` (el rol efectivo del que está mirando) y cada
// capacidad trae `grantable`. Ambos son ADITIVOS: sin ellos el front se comporta
// exactamente como antes (todo permitido), que es lo que garantiza
// normalizeCaller().

/**
 * Caller por defecto: un backend sin el campo `caller` no restringe nada.
 * @type {{can_configure: boolean, can_manage_provider: boolean, can_manage_service_account: boolean}}
 */
export const DEFAULT_CALLER = {
  can_configure: true,
  can_manage_provider: true,
  can_manage_service_account: true,
};

/**
 * Normaliza el `caller` del catálogo. Un campo ausente se lee como PERMITIDO
 * (contrato aditivo: el backend viejo omite el objeto entero); solo un `false`
 * explícito restringe.
 * @param {object} [caller]
 * @returns {object} caller con los tres booleanos resueltos
 */
export function normalizeCaller(caller) {
  const src = caller && typeof caller === "object" ? caller : {};
  return {
    ...src,
    can_configure: src.can_configure !== false,
    can_manage_provider: src.can_manage_provider !== false,
    can_manage_service_account: src.can_manage_service_account !== false,
  };
}

/**
 * true cuando la capacidad se puede conceder desde este rol. Un catálogo sin el
 * campo (`grantable` ausente) la deja concedible.
 * @param {object} capability
 * @returns {boolean}
 */
export const isGrantable = (capability) => capability?.grantable !== false;

/**
 * Traductor de ids de capacidad a etiqueta legible, armado con el catálogo.
 * Un id que el catálogo no conoce se muestra crudo: inventarle un nombre sería
 * peor que mostrar el identificador que mandó el backend.
 * @param {object} catalog
 * @returns {(id: string) => string}
 */
export function capabilityLabeler(catalog) {
  const list = Array.isArray(catalog?.capabilities) ? catalog.capabilities : [];
  const byId = new Map(
    list.filter((c) => c && typeof c.id === "string" && c.id).map((c) => [c.id, c.label || c.id]),
  );
  return (id) => byId.get(id) || String(id);
}

/**
 * Mensaje para el admin a partir de un error del backend. Devuelve "" cuando no
 * hay nada que decir (sin `code` conocido y sin `message`): el llamador decide
 * cuál es su mensaje genérico, que siempre es más específico que uno de acá.
 * @param {Error & {code?: string, capabilities?: string[]}} error
 * @param {(key: string, vars?: object) => string} t
 * @param {(id: string) => string} [capabilityLabel] etiqueta de una capacidad
 * @returns {string}
 */
export function aiErrorMessage(error, t, capabilityLabel) {
  const label = (id) => (typeof capabilityLabel === "function" ? capabilityLabel(id) : String(id));

  switch (error?.code) {
    case "AI_CAPABILITY_NOT_GRANTABLE": {
      const ids = (Array.isArray(error?.capabilities) ? error.capabilities : []).filter(Boolean);
      // Sin la lista no se puede decir QUÉ no se concedió: el texto con
      // {{list}} quedaría a medias ("Tu rol no permite conceder: .").
      if (ids.length === 0) return t("Tu rol no permite conceder esa capacidad.");
      return t("Tu rol no permite conceder: {{list}}.", { list: ids.map(label).join(", ") });
    }
    case "AI_SELF_LEARNING_AUTO_ADMIN_ONLY":
      return t("Solo un administrador puede activar el autoaprendizaje automático (sin revisión).");
    case "AI_PROVIDER_ADMIN_ONLY":
      return t("Solo un administrador puede configurar el proveedor y la credencial de la IA.");
    case "AI_AGENT_ABOVE_CALLER_SCOPE":
      return t("Este agente fue configurado con permisos mayores a los de tu rol: solo puedes pausarlo.");
    case "AI_SETTINGS_ABOVE_CALLER_SCOPE":
      return t(
        "La IA de la empresa lee pedidos y tu rol no puede conceder ese acceso: solo puedes apagarla.",
      );
    case "AI_CONFIG_FORBIDDEN_ROLE":
      return t("Tu rol no permite esta acción de la IA.");
    case "AI_CONFIG_UNVERIFIED":
      return t("No pudimos verificar tu sesión. Vuelve a iniciar sesión e inténtalo de nuevo.");
    default:
      return error?.message || "";
  }
}
